// =============================================================================
// Calendar DateTime Utilities
// Conversion utilities between native Date and @internationalized/date types
// =============================================================================

import {
  CalendarDate,
  CalendarDateTime,
  ZonedDateTime,
  Time,
  toZoned,
  toCalendarDate,
  toCalendarDateTime,
  toTime,
  getLocalTimeZone,
  today,
  now,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfYear,
  endOfYear,
  isSameDay,
  isSameMonth,
  isSameYear,
  parseDate,
  parseDateTime,
  parseTime as parseTimeString,
} from "@internationalized/date";

export {
  CalendarDate,
  CalendarDateTime,
  ZonedDateTime,
  Time,
  getLocalTimeZone,
  today,
  now,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfYear,
  endOfYear,
  isSameDay,
  isSameMonth,
  isSameYear,
  toCalendarDate,
  toCalendarDateTime,
  toZoned,
  toTime,
};

// =============================================================================
// Conversion: Native Date → Library Types
// Our parser outputs Date objects with UTC slots representing "wall clock" time
// =============================================================================

/**
 * Convert a native Date (with UTC slots as wall clock time) to CalendarDateTime
 * This is the primary conversion from our parser output
 */
export function dateToCalendarDateTime(date: Date): CalendarDateTime {
  return new CalendarDateTime(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1, // Library uses 1-indexed months
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds()
  );
}

/**
 * Convert a native Date (with UTC slots as wall clock time) to CalendarDate
 * Drops time component - useful for calendar grid
 */
export function dateToCalendarDate(date: Date): CalendarDate {
  return new CalendarDate(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate()
  );
}

/**
 * Convert a native Date (with UTC slots as wall clock time) to Time
 * Extracts only the time component
 */
export function dateToTime(date: Date): Time {
  return new Time(
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds()
  );
}

/**
 * Convert a native Date to ZonedDateTime with specified timezone
 * The Date's UTC slots are treated as wall clock time in the target timezone
 */
export function dateToZoned(date: Date, timezone: string): ZonedDateTime {
  const cdt = dateToCalendarDateTime(date);
  return toZoned(cdt, timezone);
}

// =============================================================================
// Conversion: Library Types → Native Date
// For compatibility with existing code and API output
// =============================================================================

/**
 * Convert CalendarDateTime to native Date with UTC slots
 * Inverse of dateToCalendarDateTime
 */
export function calendarDateTimeToDate(cdt: CalendarDateTime): Date {
  return new Date(
    Date.UTC(
      cdt.year,
      cdt.month - 1, // JS uses 0-indexed months
      cdt.day,
      cdt.hour,
      cdt.minute,
      cdt.second,
      cdt.millisecond
    )
  );
}

/**
 * Convert CalendarDate to native Date with UTC slots (midnight)
 */
export function calendarDateToDate(cd: CalendarDate): Date {
  return new Date(Date.UTC(cd.year, cd.month - 1, cd.day, 0, 0, 0, 0));
}

/**
 * Convert ZonedDateTime to native Date (actual UTC instant)
 */
export function zonedDateTimeToDate(zdt: ZonedDateTime): Date {
  return zdt.toDate();
}

// =============================================================================
// Conversion: String ↔ Library Types
// =============================================================================

/**
 * Parse YYYY-MM-DD string to CalendarDate
 */
export function parseCalendarDate(dateStr: string): CalendarDate | null {
  try {
    return parseDate(dateStr);
  } catch {
    return null;
  }
}

/**
 * Parse ISO datetime string to CalendarDateTime
 */
export function parseCalendarDateTime(dateTimeStr: string): CalendarDateTime | null {
  try {
    // Handle full ISO string by extracting date and time parts
    const match = dateTimeStr.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}(?::\d{2})?)/);
    if (match) {
      const date = parseDate(match[1]);
      const timePart = match[2];
      const timeParts = timePart.split(":").map(Number);
      const hours = timeParts[0] ?? 0;
      const minutes = timeParts[1] ?? 0;
      const seconds = timeParts[2] ?? 0;
      return new CalendarDateTime(date.year, date.month, date.day, hours, minutes, seconds);
    }
    return parseDateTime(dateTimeStr);
  } catch {
    return null;
  }
}

/**
 * Format CalendarDate to YYYY-MM-DD string
 */
export function formatCalendarDate(cd: CalendarDate): string {
  return cd.toString(); // CalendarDate.toString() returns "YYYY-MM-DD"
}

/**
 * Format CalendarDateTime to ISO string (without timezone)
 */
export function formatCalendarDateTime(cdt: CalendarDateTime): string {
  const pad = (n: number, len = 2) => n.toString().padStart(len, "0");
  return `${cdt.year}-${pad(cdt.month)}-${pad(cdt.day)}T${pad(cdt.hour)}:${pad(cdt.minute)}:${pad(cdt.second)}Z`;
}

/**
 * Format Time to HH:mm or HH:mm:ss string
 */
export function formatTime(time: Time, includeSeconds = false): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  if (includeSeconds) {
    return `${pad(time.hour)}:${pad(time.minute)}:${pad(time.second)}`;
  }
  return `${pad(time.hour)}:${pad(time.minute)}`;
}

// =============================================================================
// Date Math Utilities
// =============================================================================

/**
 * Add duration to CalendarDateTime
 */
export function addToDateTime(
  cdt: CalendarDateTime,
  duration: { years?: number; months?: number; days?: number; hours?: number; minutes?: number; seconds?: number }
): CalendarDateTime {
  return cdt.add(duration);
}

/**
 * Subtract duration from CalendarDateTime
 */
export function subtractFromDateTime(
  cdt: CalendarDateTime,
  duration: { years?: number; months?: number; days?: number; hours?: number; minutes?: number; seconds?: number }
): CalendarDateTime {
  return cdt.subtract(duration);
}

/**
 * Add duration to CalendarDate
 */
export function addToDate(
  cd: CalendarDate,
  duration: { years?: number; months?: number; days?: number }
): CalendarDate {
  return cd.add(duration);
}

/**
 * Compare two CalendarDates
 * Returns negative if a < b, positive if a > b, 0 if equal
 */
export function compareDates(a: CalendarDate, b: CalendarDate): number {
  return a.compare(b);
}

/**
 * Compare two CalendarDateTimes
 */
export function compareDateTimes(a: CalendarDateTime, b: CalendarDateTime): number {
  return a.compare(b);
}

// =============================================================================
// Granularity Helpers
// =============================================================================

type TimeGranularity = "date" | "hour" | "minute" | "second";

/**
 * Truncate CalendarDateTime to specified granularity
 */
export function truncateToGranularity(
  cdt: CalendarDateTime,
  granularity: TimeGranularity
): CalendarDateTime {
  switch (granularity) {
    case "date":
      return new CalendarDateTime(cdt.year, cdt.month, cdt.day, 0, 0, 0, 0);
    case "hour":
      return new CalendarDateTime(cdt.year, cdt.month, cdt.day, cdt.hour, 0, 0, 0);
    case "minute":
      return new CalendarDateTime(cdt.year, cdt.month, cdt.day, cdt.hour, cdt.minute, 0, 0);
    case "second":
      return new CalendarDateTime(cdt.year, cdt.month, cdt.day, cdt.hour, cdt.minute, cdt.second, 0);
    default:
      return cdt;
  }
}

/**
 * Extract time string based on granularity
 */
export function extractTimeString(cdt: CalendarDateTime, granularity: TimeGranularity): string {
  if (granularity === "date") return "";
  const time = new Time(cdt.hour, cdt.minute, cdt.second);
  return formatTime(time, granularity === "second");
}

// =============================================================================
// Display Formatting
// =============================================================================

/**
 * Format CalendarDateTime for display based on granularity
 */
export function formatForDisplay(cdt: CalendarDateTime, granularity: TimeGranularity): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  const datePart = `${cdt.year}-${pad(cdt.month)}-${pad(cdt.day)}`;

  switch (granularity) {
    case "date":
      return datePart;
    case "hour":
      return `${datePart} ${pad(cdt.hour)}:00`;
    case "minute":
      return `${datePart} ${pad(cdt.hour)}:${pad(cdt.minute)}`;
    case "second":
      return `${datePart} ${pad(cdt.hour)}:${pad(cdt.minute)}:${pad(cdt.second)}`;
    default:
      return datePart;
  }
}

/**
 * Format CalendarDate for display (YYYY-MM-DD)
 */
export function formatDateForDisplay(cd: CalendarDate): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${cd.year}-${pad(cd.month)}-${pad(cd.day)}`;
}
